import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Clock, Users, ChefHat } from 'lucide-react';
import { Avatar } from '../Profile/Avatar';
import { StatusIndicator } from '../StatusIndicator';
import { BookmarkButton } from '../BookmarkButton';
import { formatCategoryName } from '../../lib/utils/formatters';
import type { Recipe } from '../../types/recipes';

interface RecipeCardProps {
  recipe: Recipe;
  showStatus?: boolean;
}

export function RecipeCard({ recipe, showStatus = false }: RecipeCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const totalTime = recipe.prep_time + recipe.cook_time;

  const handleCardClick = () => {
    navigate(`/recipes/${recipe.slug}`);
  };

  return (
    <div
      onClick={handleCardClick}
      className="bg-background rounded-lg shadow-sm overflow-hidden hover:shadow-md active:scale-[0.98] active:shadow-none transition-all duration-150 cursor-pointer"
    >
      <div className="relative">
        <div className="aspect-w-16 aspect-h-9">
          <img
            src={recipe.image_url || 'https://images.unsplash.com/photo-1495521821757-a1efb6729352?auto=format&fit=crop&q=80&w=400'}
            alt={recipe.title}
            className="w-full h-48 object-cover"
            loading="lazy"
          />
        </div>
        <div className="absolute top-2 right-2" onClick={(e) => e.stopPropagation()}>
          <BookmarkButton
            targetId={recipe.id}
            targetType="recipes"
            className="bg-white/90 hover:bg-white shadow-sm"
          />
        </div>
      </div>
      
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <Link 
            to={`/profile/${recipe.user_id}/listings`}
            className="flex items-center space-x-2 group"
            onClick={(e) => e.stopPropagation()}
          >
            <Avatar 
              url={recipe.user?.avatar_url} 
              size="sm"
              userId={recipe.user_id}
              editable={false}
            />
            <span className="text-sm text-content group-hover:text-accent-text">
              {recipe.user?.username || 'Anonymous'}
            </span>
          </Link>
          {showStatus ? (
            <StatusIndicator status={recipe.approval_status} />
          ) : (
            <span className="inline-flex items-center px-3 py-1 text-xs font-medium bg-accent-text text-white rounded-full shadow-sm">
              {formatCategoryName(recipe.difficulty)}
            </span>
          )}
        </div>

        <h3 className="text-lg font-semibold text-content mb-2">
          {recipe.title}
        </h3>

        <p className="text-content/80 line-clamp-2 mb-4">
          {recipe.description}
        </p>

        <div className="flex items-center justify-between text-sm text-content/60">
          <div className="flex items-center">
            <Clock className="h-4 w-4 mr-1" />
            <span>{totalTime} {t('common.mins')}</span>
          </div>
          <div className="flex items-center">
            <Users className="h-4 w-4 mr-1" />
            <span>{recipe.servings} {t('common.servings')}</span>
          </div>
          <div className="flex items-center">
            <ChefHat className="h-4 w-4 mr-1" />
            <span>{formatCategoryName(recipe.cuisine_type)}</span>
          </div>
        </div>

        {recipe.dietary_preferences.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {recipe.dietary_preferences.slice(0, 2).map((pref, index) => (
              <span
                key={index}
                className="inline-flex items-center px-3 py-1 text-xs font-medium bg-gray-100 text-content rounded-full"
              >
                {formatCategoryName(pref)}
              </span>
            ))}
            {recipe.dietary_preferences.length > 2 && (
              <span className="inline-flex items-center px-3 py-1 text-xs font-medium bg-gray-100 text-content rounded-full">
                +{recipe.dietary_preferences.length - 2} {t('common.more')}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
